import { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Container,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { Navigate, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';
import { getPokerStudyAccessContext } from '../services/pokerStudyService';
import type { PokerStudyAccessContext } from '../types/pokerStudy';

export default function PokerStudyProtectedRoute() {
  const { user, isLoading, industrialCostsRole, signOut } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const userId = user?.id ?? null;

  const [accessContext, setAccessContext] = useState<PokerStudyAccessContext | null>(null);
  const [hasCheckedAccess, setHasCheckedAccess] = useState(false);
  const [accessErrorMessage, setAccessErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    let isMounted = true;

    async function loadAccessContext() {
      if (!userId) {
        setAccessContext(null);
        setHasCheckedAccess(true);
        return;
      }

      setAccessErrorMessage(null);

      try {
        const context = await getPokerStudyAccessContext();

        if (!isMounted) {
          return;
        }

        setAccessContext(context);
      } catch (error) {
        console.error(error);

        if (!isMounted) {
          return;
        }

        setAccessContext(null);
        setAccessErrorMessage('No se pudo comprobar tu acceso a PokerStudy.');
      } finally {
        if (isMounted) {
          setHasCheckedAccess(true);
        }
      }
    }

    if (!isLoading) {
      setHasCheckedAccess(false);
      void loadAccessContext();
    }

    return () => {
      isMounted = false;
    };
  }, [isLoading, userId]);

  if (isLoading || !hasCheckedAccess) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          bgcolor: '#0f172a',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (!user) {
    return <Navigate to="/poker/login" state={{ from: location }} replace />;
  }

  if (industrialCostsRole === 'factory_operator') {
    return <Navigate to="/factory/operator" replace />;
  }

  if (industrialCostsRole === 'factory_manager') {
    return <Navigate to="/industrial-cost" replace />;
  }

  if (accessErrorMessage || !accessContext?.has_access || !accessContext.is_active) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          bgcolor: '#0f172a',
          color: '#f8fafc',
          display: 'flex',
          alignItems: 'center',
        }}
      >
        <Container maxWidth="sm">
          <Paper
            elevation={0}
            sx={{
              p: { xs: 3, sm: 5 },
              borderRadius: 4,
              bgcolor: '#1e293b',
              border: '1px solid #334155',
              boxShadow: '0 0 40px rgba(0, 0, 0, 0.4)',
            }}
          >
            <Stack spacing={2.5}>
              <Box>
                <Typography
                  variant="overline"
                  sx={{
                    color: '#fcd34d',
                    fontWeight: 900,
                    letterSpacing: 2,
                  }}
                >
                  ACCESO NO HABILITADO
                </Typography>

                <Typography variant="h5" sx={{ mt: 1, fontWeight: 850, color: '#f8fafc' }}>
                  Tu cuenta no tiene acceso activo a PokerStudy.
                </Typography>
              </Box>

              <Alert
                severity={accessErrorMessage ? 'error' : 'warning'}
                variant="outlined"
                sx={{
                  borderColor: accessErrorMessage
                    ? 'rgba(248, 113, 113, 0.5)'
                    : 'rgba(252, 211, 77, 0.45)',
                  color: accessErrorMessage ? '#fca5a5' : '#fde68a',
                  '& .MuiAlert-icon': {
                    color: accessErrorMessage ? '#f87171' : '#fcd34d',
                  },
                }}
              >
                {accessErrorMessage ||
                  'La sesión es válida, pero no existe una autorización PokerStudy activa para este usuario.'}
              </Alert>

              <Typography variant="body2" sx={{ color: '#94a3b8', lineHeight: 1.7 }}>
                Si crees que deberías tener acceso, revisa que el usuario esté dado de alta en
                ps_user_access con is_active = true.
              </Typography>

              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25}>
                <Button
                  variant="contained"
                  onClick={() => navigate('/poker/login', { replace: true })}
                  sx={{
                    fontWeight: 900,
                    bgcolor: '#38bdf8',
                    color: '#0f172a',
                    '&:hover': { bgcolor: '#0ea5e9' },
                  }}
                >
                  Volver al login
                </Button>

                <Button
                  variant="outlined"
                  onClick={async () => {
                    await signOut();
                    navigate('/poker/login', { replace: true });
                  }}
                  sx={{
                    fontWeight: 900,
                    color: '#cbd5e1',
                    borderColor: alpha('#94a3b8', 0.35),
                    '&:hover': {
                      borderColor: '#f8fafc',
                      bgcolor: alpha('#fff', 0.03),
                    },
                  }}
                >
                  Cerrar sesión
                </Button>
              </Stack>
            </Stack>
          </Paper>
        </Container>
      </Box>
    );
  }

  return <Outlet />;
}